/**
 * API helpers for the DocumentTemplate authoring UI.
 * Wraps raw `fetch` with JWT auth and a consistent error shape.
 */
import { tokenManager } from '../../lib/api'
import type { Job } from '../../hooks/useJobPoll'
import type {
  DocumentTemplate,
  OwnerLevel,
  ResolvedTemplate,
  Sample,
  Scope,
  Section,
  SectionSpecMembership,
  SkillManifest,
  Spec,
} from './types'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

function authHeaders(): HeadersInit {
  const token = tokenManager.getAccessToken()
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

async function jsonFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  const resp = await fetch(url, {
    ...init,
    headers: { ...authHeaders(), ...(init.headers || {}) },
  })
  if (!resp.ok) {
    let detail: any = null
    try { detail = await resp.json() } catch { /* ignore */ }
    const msg = (detail && (detail.detail || detail.error)) || `HTTP ${resp.status}`
    throw new Error(msg)
  }
  if (resp.status === 204) return undefined as any
  return resp.json()
}

function unwrapList<T>(payload: any): T[] {
  return Array.isArray(payload) ? payload : (payload.results || [])
}

// ── Templates ────────────────────────────────────────────────────────────

function scopeQuery(scope: Scope): string {
  const params = new URLSearchParams({ owner_level: scope.level })
  if (scope.level === 'office' && scope.ownerId) params.set('owner_office', String(scope.ownerId))
  if (scope.level === 'program_manager' && scope.ownerId) params.set('owner_user', String(scope.ownerId))
  if (scope.level === 'program' && scope.ownerId) params.set('owner_program', String(scope.ownerId))
  return params.toString()
}

export async function listTemplatesAtScope(scope: Scope): Promise<DocumentTemplate[]> {
  const qs = scopeQuery(scope)
  const payload = await jsonFetch<any>(`${API_BASE_URL}/api/documents/templates/?${qs}`)
  return unwrapList<DocumentTemplate>(payload)
}

export async function listSystemTemplates(): Promise<DocumentTemplate[]> {
  const payload = await jsonFetch<any>(`${API_BASE_URL}/api/documents/templates/?owner_level=system`)
  return unwrapList<DocumentTemplate>(payload)
}

/** All templates visible at the given scope (cascades upward from the scope
 * to system). The caller dedupes by key and picks the most-specific leaf. */
export async function listVisibleTemplates(args: {
  scope_level: OwnerLevel
  office_id?: number | null
  user_id?: number | null
  program_id?: number | null
  /** Also return inactive (draft) templates owned by the scope. Template managers only. */
  include_drafts?: boolean
}): Promise<DocumentTemplate[]> {
  const qs = new URLSearchParams({ scope_level: args.scope_level })
  if (args.office_id) qs.set('office_id', String(args.office_id))
  if (args.user_id) qs.set('user_id', String(args.user_id))
  if (args.program_id) qs.set('program_id', String(args.program_id))
  if (args.include_drafts) qs.set('include_drafts', 'true')
  const payload = await jsonFetch<any>(`${API_BASE_URL}/api/documents/templates/visible/?${qs.toString()}`)
  return Array.isArray(payload) ? payload : (payload.results || [])
}

export async function getTemplate(id: string): Promise<DocumentTemplate> {
  return jsonFetch<DocumentTemplate>(`${API_BASE_URL}/api/documents/templates/${id}/`)
}

/** Returns the resolved effective view of a template — sections/questions
 * walked via cascade using the template's own scope context. Used by the
 * authoring UI so a customized child template still shows its inherited
 * sections instead of appearing empty. */
export async function getEffectiveTemplate(id: string): Promise<ResolvedTemplate> {
  return jsonFetch<ResolvedTemplate>(`${API_BASE_URL}/api/documents/templates/${id}/effective/`)
}

export async function createTemplate(data: Partial<DocumentTemplate> & { key: string; name: string; owner_level: OwnerLevel }): Promise<DocumentTemplate> {
  return jsonFetch<DocumentTemplate>(`${API_BASE_URL}/api/documents/templates/`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Upload a PDF or DOCX file to become a draft template (office scope).
 * Returns the job to poll; the draft only has sections once the job completes.
 */
export async function importTemplate(file: File, officeId: number): Promise<{ job_id: string; template_id: string }> {
  const formData = new FormData()
  formData.set('file', file)
  formData.set('owner_office', String(officeId))
  const resp = await fetch(`${API_BASE_URL}/api/documents/templates/import/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
    body: formData,
  })
  if (!resp.ok) {
    // A 413 comes from a proxy, before the backend: its body isn't our JSON.
    if (resp.status === 413) throw new Error('This file is too large to upload (limit 16 MB).')
    let detail: any = null
    try { detail = await resp.json() } catch { /* ignore */ }
    throw new Error((detail && (detail.detail || detail.error)) || `HTTP ${resp.status}`)
  }
  return resp.json()
}

/** Poll an import job until it finishes; resolves with the draft template's id.
 * ``onUpdate`` gets each polled job, e.g. to drive a progress card. */
export async function waitForTemplateImport(
  jobId: string,
  { intervalMs = 2000, timeoutMs = 16 * 60_000, onUpdate }: { intervalMs?: number; timeoutMs?: number; onUpdate?: (job: Job) => void } = {},
): Promise<string> {
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const job = await jsonFetch<Job>(`${API_BASE_URL}/api/ai/jobs/${jobId}/`)
    onUpdate?.(job)
    const templateId = job.result_summary?.template_id
    if (job.status === 'completed' && typeof templateId === 'string') return templateId
    if (job.status === 'failed' || job.status === 'canceled') {
      throw new Error(job.error_message || 'Template import failed')
    }
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  throw new Error('Import is taking longer than expected. Check the template list again in a minute.')
}

export async function updateTemplate(id: string, data: Partial<DocumentTemplate>): Promise<DocumentTemplate> {
  return jsonFetch<DocumentTemplate>(`${API_BASE_URL}/api/documents/templates/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteTemplate(id: string): Promise<void> {
  await jsonFetch<void>(`${API_BASE_URL}/api/documents/templates/${id}/`, { method: 'DELETE' })
}

export async function customizeTemplate(parentId: string, body: { owner_level: OwnerLevel; owner_office?: number; owner_user?: number; owner_program?: number }): Promise<DocumentTemplate> {
  return jsonFetch<DocumentTemplate>(`${API_BASE_URL}/api/documents/templates/${parentId}/customize/`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

// ── Sections ─────────────────────────────────────────────────────────────

export async function createSection(data: {
  template: string
  key: string
  title: string
  order: number
  owner_level: OwnerLevel
  owner_office?: number | null
  owner_user?: number | null
  owner_program?: number | null
  parent?: string | null
  description?: string | null
  writing_agent_instructions?: string | null
}): Promise<Section> {
  return jsonFetch<Section>(`${API_BASE_URL}/api/documents/template-sections/`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateSection(id: string, data: Partial<Section>): Promise<Section> {
  return jsonFetch<Section>(`${API_BASE_URL}/api/documents/template-sections/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteSection(id: string): Promise<void> {
  await jsonFetch<void>(`${API_BASE_URL}/api/documents/template-sections/${id}/`, { method: 'DELETE' })
}

/** Customize an inherited section at the current scope. Creates a child
 * section whose parent is this one, and copies the memberships over so
 * the resulting section has the inherited question set as its starting
 * point. */
export async function customizeSection(parentId: string, body: {
  owner_level: OwnerLevel
  owner_office?: number
  owner_user?: number
  owner_program?: number
}): Promise<Section> {
  return jsonFetch<Section>(`${API_BASE_URL}/api/documents/template-sections/${parentId}/customize/`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** Clone an existing section (with all memberships) into a different
 * template — used for composite documents that reuse sections across
 * templates. Same Question rows are referenced, so answers flow through. */
export async function cloneSectionToTemplate(sourceId: string, body: {
  target_template: string
  owner_level: OwnerLevel
  owner_office?: number
  owner_user?: number
  owner_program?: number
  new_key?: string
}): Promise<Section> {
  return jsonFetch<Section>(`${API_BASE_URL}/api/documents/template-sections/${sourceId}/clone-to-template/`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** Return all registered writing skills with their UI manifests.
 * Cached by the caller — this list changes only on backend deploys. */
export async function fetchSkills(): Promise<SkillManifest[]> {
  const payload = await jsonFetch<any>(`${API_BASE_URL}/api/documents/template-sections/skills/`)
  return Array.isArray(payload) ? payload : (payload.results || [])
}

/** All sections visible at the given scope via cascade — used by the
 * "add existing section" picker. */
export async function listVisibleSections(args: {
  scope_level: OwnerLevel
  office_id?: number | null
  user_id?: number | null
  program_id?: number | null
}): Promise<Section[]> {
  const qs = new URLSearchParams({ scope_level: args.scope_level })
  if (args.office_id) qs.set('office_id', String(args.office_id))
  if (args.user_id) qs.set('user_id', String(args.user_id))
  if (args.program_id) qs.set('program_id', String(args.program_id))
  const payload = await jsonFetch<any>(`${API_BASE_URL}/api/documents/template-sections/visible/?${qs.toString()}`)
  return Array.isArray(payload) ? payload : (payload.results || [])
}

// ── Specs (the unified question + structured-spec library) ───────────────
// CRUD lives on the programs app at /api/program-specs/ (the old
// /api/documents/questions/ routes were removed in the migration).

export async function createSpec(data: {
  key: string
  owner_level: OwnerLevel
  owner_office?: number | null
  owner_user?: number | null
  owner_program?: number | null
  parent?: number | null
  name?: string | null
  prompt?: string | null
  guidance?: string | null
  spec_type?: string | null
  category?: string | null
  unit?: string | null
  category_options?: { value: string; label: string }[] | null
  eligible_respondent_roles?: string[] | null
  interview_priority?: string | null
}): Promise<Spec> {
  return jsonFetch<Spec>(`${API_BASE_URL}/api/program-specs/`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateSpec(id: number, data: Partial<Spec>): Promise<Spec> {
  return jsonFetch<Spec>(`${API_BASE_URL}/api/program-specs/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteSpec(id: number): Promise<void> {
  await jsonFetch<void>(`${API_BASE_URL}/api/program-specs/${id}/`, { method: 'DELETE' })
}

/** Customize an inherited spec at the current scope. */
export async function customizeSpec(parentId: number, body: {
  owner_level: OwnerLevel
  owner_office?: number
  owner_user?: number
  owner_program?: number
}): Promise<Spec> {
  return jsonFetch<Spec>(`${API_BASE_URL}/api/program-specs/${parentId}/customize/`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** All specs visible at the given scope via cascade — used by the
 * "add existing spec" picker. Same spec may appear in multiple documents;
 * attaching a visible spec to a section maps its prior answers through. */
export async function listVisibleSpecs(args: {
  scope_level: OwnerLevel
  office_id?: number | null
  user_id?: number | null
  program_id?: number | null
}): Promise<Spec[]> {
  const qs = new URLSearchParams({ scope_level: args.scope_level })
  if (args.office_id) qs.set('office_id', String(args.office_id))
  if (args.user_id) qs.set('user_id', String(args.user_id))
  if (args.program_id) qs.set('program_id', String(args.program_id))
  const payload = await jsonFetch<any>(`${API_BASE_URL}/api/program-specs/visible/?${qs.toString()}`)
  return Array.isArray(payload) ? payload : (payload.results || [])
}

// ── Section ⇄ Spec memberships ───────────────────────────────────────────
// The through-model endpoint keeps its legacy path (/section-questions/) but
// now carries a `spec` FK.

export async function attachSpecToSection(data: {
  section: string
  spec: number
  order: number
  required: boolean
}): Promise<SectionSpecMembership> {
  return jsonFetch<SectionSpecMembership>(`${API_BASE_URL}/api/documents/section-questions/`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateMembership(id: number, data: Partial<SectionSpecMembership>): Promise<SectionSpecMembership> {
  return jsonFetch<SectionSpecMembership>(`${API_BASE_URL}/api/documents/section-questions/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function detachSpecFromSection(id: number): Promise<void> {
  await jsonFetch<void>(`${API_BASE_URL}/api/documents/section-questions/${id}/`, { method: 'DELETE' })
}

/** List memberships for a specific section — used when we need to find
 * the child's copy of a membership after auto-customizing a section. */
export async function listMembershipsForSection(sectionId: string): Promise<SectionSpecMembership[]> {
  const payload = await jsonFetch<any>(`${API_BASE_URL}/api/documents/section-questions/?section=${sectionId}`)
  return Array.isArray(payload) ? payload : (payload.results || [])
}

// ── Samples ──────────────────────────────────────────────────────────────

export async function uploadSample(data: {
  template: string
  title: string
  file: File | null
  notes?: string
}): Promise<Sample> {
  const token = tokenManager.getAccessToken()
  const formData = new FormData()
  formData.set('template', data.template)
  formData.set('title', data.title)
  if (data.notes) formData.set('notes', data.notes)
  if (data.file) formData.set('file', data.file)
  const resp = await fetch(`${API_BASE_URL}/api/documents/samples/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  })
  if (!resp.ok) {
    let detail: any = null
    try { detail = await resp.json() } catch { /* ignore */ }
    const msg = (detail && (detail.detail || detail.error)) || `HTTP ${resp.status}`
    throw new Error(msg)
  }
  return resp.json()
}

export async function updateSample(id: string, data: Partial<Sample>): Promise<Sample> {
  return jsonFetch<Sample>(`${API_BASE_URL}/api/documents/samples/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteSample(id: string): Promise<void> {
  await jsonFetch<void>(`${API_BASE_URL}/api/documents/samples/${id}/`, { method: 'DELETE' })
}

// ── Program documents (generation + sections + assets) ───────────────────

export interface ProgramDocumentSection {
  id: string
  program_document: string
  template_section: string
  section_key: string
  title: string
  order: number
  content_markdown: string
  status: 'pending' | 'generating' | 'generated' | 'edited' | 'stale' | 'failed'
  locked: boolean
  last_edited_by: 'agent' | 'analyst' | null
  last_generated_at: string | null
  last_edited_at: string | null
  source_question_ids: string[]
  source_section_keys: string[]
  source_context_refs: Array<Record<string, unknown>>
  generation_metadata: Record<string, unknown>
  version: number
  error: string
}

export interface ProgramDocumentBrief {
  id: string
  program: number
  template: string
  template_key: string
  status: string
  created_at: string
  updated_at: string
}

export type GenerationJobStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'partial_failure'
  | 'failed'
  | 'canceled'

export interface DocumentGenerationJob {
  id: string
  program_document: string
  status: GenerationJobStatus
  kind: 'full' | 'partial'
  requested_section_keys: string[]
  force: boolean
  celery_task_id: string
  total_sections: number
  current_section_key: string
  completed_section_keys: string[]
  failed_section_keys: string[]
  skipped_section_keys: string[]
  error_summary: string
  started_by: number | null
  queued_at: string
  started_at: string | null
  completed_at: string | null
  updated_at: string
  is_active: boolean
}

export interface GenerationPlanEntry {
  section_key: string
  title: string
  order: number
  writing_skill: string
  skill_config: Record<string, unknown>
  depends_on: string[]
  depends_on_all: boolean
}

export async function ensureProgramDocument(
  programId: number,
  templateIdOrKey: { templateId?: string; templateKey?: string },
): Promise<ProgramDocumentBrief> {
  const body: Record<string, unknown> = { program: programId }
  // Prefer template_id (unambiguous) over template_key (which needs a
  // scope-aware root lookup on the backend).
  if (templateIdOrKey.templateId) body.template = templateIdOrKey.templateId
  else if (templateIdOrKey.templateKey) body.template_key = templateIdOrKey.templateKey
  return jsonFetch<ProgramDocumentBrief>(
    `${API_BASE_URL}/api/documents/program-documents/ensure/`,
    { method: 'POST', body: JSON.stringify(body) },
  )
}

export async function listProgramDocuments(programId: number): Promise<ProgramDocumentBrief[]> {
  const payload = await jsonFetch<any>(
    `${API_BASE_URL}/api/documents/program-documents/?program=${programId}`,
  )
  return Array.isArray(payload) ? payload : payload.results || []
}

export async function getProgramDocumentSections(
  programDocumentId: string,
): Promise<ProgramDocumentSection[]> {
  return jsonFetch<ProgramDocumentSection[]>(
    `${API_BASE_URL}/api/documents/program-documents/${programDocumentId}/sections/`,
  )
}

/** Returns the effective template resolved from THIS PROGRAM's perspective.
 * Distinct from `getEffectiveTemplate`, which resolves a template against its
 * own scope for the authoring UI. Sections carry `description`, `writing_skill`
 * and cascade-resolved `questions`, so the editor can show a section's source
 * questions without a second round of spec lookups. */
export async function getProgramDocumentEffectiveTemplate(
  programDocumentId: string,
): Promise<ResolvedTemplate> {
  return jsonFetch<ResolvedTemplate>(
    `${API_BASE_URL}/api/documents/program-documents/${programDocumentId}/effective-template/`,
  )
}

export async function getGenerationPlan(programDocumentId: string): Promise<GenerationPlanEntry[]> {
  const payload = await jsonFetch<{ plan: GenerationPlanEntry[] }>(
    `${API_BASE_URL}/api/documents/program-documents/${programDocumentId}/generation-plan/`,
  )
  return payload.plan || []
}

export class GenerationConflictError extends Error {
  activeJob: DocumentGenerationJob | null
  constructor(message: string, activeJob: DocumentGenerationJob | null) {
    super(message)
    this.name = 'GenerationConflictError'
    this.activeJob = activeJob
  }
}

async function postGeneration(url: string, body: unknown): Promise<DocumentGenerationJob> {
  const token = tokenManager.getAccessToken()
  const resp = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (resp.status === 409) {
    let detail: any = null
    try { detail = await resp.json() } catch { /* ignore */ }
    throw new GenerationConflictError(
      detail?.error || 'A generation is already running for this document.',
      detail?.active_job || null,
    )
  }
  if (!resp.ok) {
    let detail: any = null
    try { detail = await resp.json() } catch { /* ignore */ }
    throw new Error(detail?.error || detail?.detail || `HTTP ${resp.status}`)
  }
  return resp.json()
}

export async function generateProgramDocument(
  programDocumentId: string,
  options?: { section_keys?: string[]; force?: boolean },
): Promise<DocumentGenerationJob> {
  return postGeneration(
    `${API_BASE_URL}/api/documents/program-documents/${programDocumentId}/generate/`,
    options || {},
  )
}

export async function regenerateSection(
  programDocumentId: string,
  sectionKey: string,
  options?: { force?: boolean },
): Promise<DocumentGenerationJob> {
  return postGeneration(
    `${API_BASE_URL}/api/documents/program-documents/${programDocumentId}/sections/${sectionKey}/regenerate/`,
    options || {},
  )
}

export async function getActiveGenerationJob(
  programDocumentId: string,
): Promise<DocumentGenerationJob | null> {
  const payload = await jsonFetch<{ job: DocumentGenerationJob | null }>(
    `${API_BASE_URL}/api/documents/program-documents/${programDocumentId}/generation-jobs/active/`,
  )
  return payload.job
}

export async function listGenerationJobs(
  programDocumentId: string,
): Promise<DocumentGenerationJob[]> {
  return jsonFetch<DocumentGenerationJob[]>(
    `${API_BASE_URL}/api/documents/program-documents/${programDocumentId}/generation-jobs/`,
  )
}

export async function updateProgramSection(
  sectionId: string,
  data: Partial<Pick<ProgramDocumentSection, 'content_markdown' | 'locked'>>,
): Promise<ProgramDocumentSection> {
  return jsonFetch<ProgramDocumentSection>(
    `${API_BASE_URL}/api/documents/program-sections/${sectionId}/`,
    { method: 'PATCH', body: JSON.stringify(data) },
  )
}

export function exportProgramDocumentUrl(
  programDocumentId: string,
  format: 'md' | 'docx' | 'pdf',
): string {
  // The query param is `export_format`, not `format` — DRF reserves
  // `format` for content-negotiation and 404s on unknown renderer names.
  return `${API_BASE_URL}/api/documents/program-documents/${programDocumentId}/export/?export_format=${format}`
}

export async function uploadAsset(
  programDocumentId: string,
  file: File,
  options?: { asset_type?: string; caption?: string; alt_text?: string },
): Promise<{ id: string; file_url: string }> {
  const token = tokenManager.getAccessToken()
  const formData = new FormData()
  formData.set('program_document', programDocumentId)
  formData.set('asset_type', options?.asset_type || 'image')
  formData.set('file', file)
  if (options?.caption) formData.set('caption', options.caption)
  if (options?.alt_text) formData.set('alt_text', options.alt_text)
  const resp = await fetch(`${API_BASE_URL}/api/documents/assets/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  })
  if (!resp.ok) {
    let detail: any = null
    try { detail = await resp.json() } catch { /* ignore */ }
    const msg = (detail && (detail.detail || detail.error)) || `HTTP ${resp.status}`
    throw new Error(msg)
  }
  return resp.json()
}
